import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container, CircularProgress, ThemeProvider, Typography,
} from '@material-ui/core';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useForm, Controller } from 'react-hook-form';

import { useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';
import {
  ContentContainer, HeaderBox, Title,
} from './ActivationSteps.styles';

import sleepioTheme from '@/themes/sleepioTheme';
import Button from '@/components/Button';
import { TextField } from '@/components';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { logError } from '@/utils/logError';
import { formatDateInput } from '@/utils/inputHelpers';
import { formatDateString, parseDate } from '@/utils/stringUtils';
import {
  getAge, validateZipCode, validateEmployeeId,
} from '@/utils/stringValidators';
import { SLEEPIO_PDT_UID } from '@/utils/constants';
import { ELIGIBILITY_CONFIGURATION_QUERY, CHECK_ELIGIBILITY_QUERY } from '@/apollo/queries';
import { CONSUMER_APP_SAVE_COVERAGE_DETAILS } from '@/apollo/mutations';

// productId for Sleepio is 1 (matches getProductId('sleepio'))
const SLEEPIO_PRODUCT_ID = 1;
const MIN_AGE = 18;

type FormValues = {
  firstName: string;
  lastName: string;
  dob: string;
  zipCode: string;
  employeeId: string;
};

const CoverageFormInner: React.FC = () => {
  useAcceptedLanguage();

  const { t } = useTranslation();
  const { state, dispatch } = useActivation();
  const history = useHistory();
  const base = useActivationBase();

  const [sessionExpired, setSessionExpired] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isIneligible, setIsIneligible] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const arrivalTimestampRef = useRef(new Date().toISOString());

  const {
    control,
    handleSubmit,
    watch,
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      dob: '',
      zipCode: '',
      employeeId: '',
    },
  });

  const values = watch();

  // Check for an active Supabase session on mount. If none, redirect back to auth.
  // history and base are stable references — intentionally omitted from deps.
  useEffect(() => {
    getSupabaseClient().auth.getSession().then(({ data: { session } }) => {
      if (!session?.access_token) {
        setSessionExpired(true);
        history.replace(`${base}/auth`);
        return;
      }
      setAccessToken(session.access_token);
    });
  }, []);

  // Fetch org eligibility config to determine which optional fields to show.
  const { data: configData, loading: configLoading } = useQuery(ELIGIBILITY_CONFIGURATION_QUERY, {
    variables: {
      organizationSlug: state.orgSlug,
      pdtUid: SLEEPIO_PDT_UID,
    },
    skip: !accessToken || !state.orgSlug,
    context: accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined,
  });

  const config = configData?.eligibilityConfiguration;
  const showZipCode = config?.collectZipcodeForEligibility ?? false;
  const showEmployeeId = config?.hasEmployeeId ?? false;
  const employeeIdLabel = config?.employeeIdFieldRename || t('coverage.sleepio.patient.employee_id_label');

  const [checkEligibility] = useLazyQuery(CHECK_ELIGIBILITY_QUERY, { fetchPolicy: 'no-cache' });
  const [saveCoverageDetails] = useMutation(CONSUMER_APP_SAVE_COVERAGE_DETAILS);

  const isDobValid = values.dob.length === 14 && (() => {
    const parsed = parseDate(values.dob);
    const date = new Date(parsed);
    return !Number.isNaN(date.getTime()) && date.getFullYear() >= 1900 && getAge(values.dob) >= MIN_AGE;
  })();

  const isFormValid = !sessionExpired
    && values.firstName.trim() !== ''
    && values.lastName.trim() !== ''
    && isDobValid
    && (!showZipCode || validateZipCode(values.zipCode))
    && (!showEmployeeId || values.employeeId.trim() !== '');

  const onSubmit = async (data: FormValues) => {
    setGeneralError('');
    setIsSubmitting(true);

    try {
      // Re-check session at submit time — it may have expired while the form was open.
      const { data: { session } } = await getSupabaseClient().auth.getSession();
      const token = session?.access_token;

      if (!token) {
        setSessionExpired(true);
        setIsSubmitting(false);
        return;
      }

      const authContext = { headers: { Authorization: `Bearer ${token}` } };

      // Step 1: Check eligibility.
      const eligibilityResult = await checkEligibility({
        variables: {
          pdtUid: SLEEPIO_PDT_UID,
          firstName: data.firstName.trim(),
          lastName: data.lastName.trim(),
          dateOfBirth: formatDateString(data.dob),
          organizationSlug: state.orgSlug,
          productId: SLEEPIO_PRODUCT_ID,
          postalCode: showZipCode ? data.zipCode : undefined,
          employeeId: showEmployeeId ? data.employeeId.trim() : undefined,
        },
        context: authContext,
      });

      if (eligibilityResult?.error) {
        logError('[CoverageForm]', 'eligibility check failed', eligibilityResult.error);
        setGeneralError(t('coverage.pre_onboarding.sleepio.activation.coverage_form.error_generic'));
        setIsSubmitting(false);
        return;
      }

      if (eligibilityResult?.data?.eligibilityStatus?.isEligible === false) {
        setIsIneligible(true);
        setIsSubmitting(false);
        return;
      }

      if (!eligibilityResult?.data?.eligibilityStatus?.isEligible) {
        // isEligible is null/undefined — indeterminate server response.
        logError('[CoverageForm]', 'eligibility check returned indeterminate result', eligibilityResult?.data);
        setGeneralError(t('coverage.pre_onboarding.sleepio.activation.coverage_form.error_generic'));
        setIsSubmitting(false);
        return;
      }

      // Step 2: Save coverage details.
      const saveResponse = await saveCoverageDetails({
        variables: {
          pdtUid: SLEEPIO_PDT_UID,
          firstName: data.firstName.trim(),
          lastName: data.lastName.trim(),
          dateOfBirth: formatDateString(data.dob),
          coverageCheckTimestamp: new Date().toISOString(),
          coverageCheckStartTimestamp: arrivalTimestampRef.current,
          coverageCheckStatus: 'pass',
          postalCode: showZipCode ? data.zipCode : undefined,
          employeeId: showEmployeeId ? data.employeeId.trim() : undefined,
        },
        context: authContext,
      });

      if (!saveResponse.data?.consumerAppSaveCoverageDetails?.success) {
        logError('[CoverageForm]', 'saveCoverageDetails returned success=false');
        setGeneralError(t('coverage.pre_onboarding.sleepio.activation.coverage_form.error_generic'));
        setIsSubmitting(false);
        return;
      }

      // Advance to confirm-and-handoff sequence.
      dispatch({ type: 'SET_STEP', step: 'activation_complete' });
      const redirectParam = state.redirectTo
        ? `?redirectTo=${encodeURIComponent(state.redirectTo)}`
        : '';
      history.push(`${base}/complete${redirectParam}`);
    } catch (err) {
      logError('[CoverageForm]', 'handleSubmit failed', err);
      setGeneralError(t('coverage.pre_onboarding.sleepio.activation.coverage_form.error_generic'));
      setIsSubmitting(false);
    }
  };

  if (sessionExpired) {
    return (
      <ContentContainer>
        <SleepioHeader />
        <Container maxWidth="xs" style={{ flex: 1, paddingTop: 32 }}>
          <Typography variant="h2" color="textPrimary" style={{ marginBottom: 16 }} data-testid="session-expired-title">
            {t('coverage.pre_onboarding.sleepio.activation.coverage_form.session_expired_title')}
          </Typography>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.coverage_form.session_expired_body')}
          </Typography>
        </Container>
        <Footer />
      </ContentContainer>
    );
  }

  if (isIneligible) {
    return (
      <ContentContainer>
        <SleepioHeader />
        <Container maxWidth="xs" style={{ flex: 1, paddingTop: 32 }}>
          <Typography variant="h2" color="textPrimary" style={{ marginBottom: 16 }} data-testid="ineligible-title">
            {t('coverage.pre_onboarding.sleepio.activation.coverage_form.ineligible_title')}
          </Typography>
          <Typography color="textPrimary" style={{ marginBottom: 24 }}>
            {t('coverage.pre_onboarding.sleepio.activation.coverage_form.ineligible_body')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            rounded
            data-testid="retry-button"
            onClick={() => setIsIneligible(false)}
          >
            {t('coverage.pre_onboarding.sleepio.activation.coverage_form.ineligible_retry')}
          </Button>
        </Container>
        <Footer />
      </ContentContainer>
    );
  }

  if (!accessToken || configLoading) {
    return (
      <ContentContainer>
        <SleepioHeader />
        <Container
          maxWidth="xs"
          style={{
            flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center',
          }}
        >
          <CircularProgress color="primary" data-testid="loading-spinner" />
        </Container>
        <Footer />
      </ContentContainer>
    );
  }

  return (
    <ContentContainer>
      <SleepioHeader />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.coverage_form.title')}
          </Title>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.coverage_form.subtitle')}
          </Typography>
        </HeaderBox>

        <Controller
          name="firstName"
          control={control}
          rules={{
            validate: (v) => v.trim() !== '' || t('coverage.errors.first_name_error'),
          }}
          render={({ field, fieldState }) => (
            <TextField
              required
              label={t('coverage.sleepio.patient.f_name_label')}
              inputProps={{ 'data-testid': 'first-name-input' }}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              onBlur={field.onBlur}
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? ''}
            />
          )}
        />

        <Controller
          name="lastName"
          control={control}
          rules={{
            validate: (v) => v.trim() !== '' || t('coverage.errors.last_name_error'),
          }}
          render={({ field, fieldState }) => (
            <TextField
              required
              label={t('coverage.sleepio.patient.l_name_label')}
              inputProps={{ 'data-testid': 'last-name-input' }}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              onBlur={field.onBlur}
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? ''}
            />
          )}
        />

        {showZipCode && (
          <Controller
            name="zipCode"
            control={control}
            rules={{
              validate: (v) => validateZipCode(v) || t('coverage.errors.zipcode_error'),
            }}
            render={({ field, fieldState }) => (
              <TextField
                required
                label={t('coverage.sleepio.patient.zipcode_label')}
                inputProps={{ 'data-testid': 'zipcode-input' }}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? ''}
              />
            )}
          />
        )}

        {showEmployeeId && (
          <Controller
            name="employeeId"
            control={control}
            rules={{
              validate: (v) => {
                const genericError = `${t('coverage.errors.employee_id_error')} ${employeeIdLabel}`;
                if (!v.trim()) { return genericError; }
                const result = validateEmployeeId(v.trim(), {
                  validationEnabled: config?.employeeIdValidationEnabled,
                  allowedChars: config?.employeeIdAllowedChars,
                  minLength: config?.employeeIdMinLength,
                  maxLength: config?.employeeIdMaxLength,
                  errorMessage: config?.employeeIdValidationErrorMessage,
                });
                return result.isValid || result.errorMessage || genericError;
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                required
                label={employeeIdLabel}
                inputProps={{
                  placeholder: config?.employeeIdExample || employeeIdLabel,
                  'data-testid': 'employee-id-input',
                }}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? ''}
              />
            )}
          />
        )}

        <Controller
          name="dob"
          control={control}
          rules={{
            validate: (v) => {
              if (!v) { return true; }
              if (v.length < 14) { return t('coverage.errors.dob_error'); }
              const parsed = parseDate(v);
              const date = new Date(parsed);
              const isValidDate = !Number.isNaN(date.getTime()) && date.getFullYear() >= 1900;
              if (!isValidDate) { return t('coverage.errors.dob_error'); }
              if (getAge(v) < MIN_AGE) { return t('coverage.sleepio.patient.dob_age_error'); }
              return true;
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              required
              label={t('coverage.sleepio.patient.dob_label')}
              inputProps={{
                placeholder: 'MM / DD / YYYY',
                maxLength: 14,
                inputMode: 'numeric' as const,
                'data-testid': 'dob-input',
              }}
              value={field.value}
              onChange={(e) => field.onChange(formatDateInput(e.target.value))}
              onBlur={field.onBlur}
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? ''}
            />
          )}
        />

        {generalError && (
          <Typography
            color="error"
            style={{ marginTop: 8 }}
            data-testid="general-error"
          >
            {generalError}
          </Typography>
        )}

        <Button
          variant="contained"
          color="primary"
          fullWidth
          rounded
          type="button"
          data-testid="submit-button"
          disabled={!isFormValid || isSubmitting}
          loading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
          style={{ marginTop: 24, borderRadius: 100 }}
        >
          {t('coverage.pre_onboarding.sleepio.activation.coverage_form.submit')}
        </Button>
      </Container>
      <Footer />
    </ContentContainer>
  );
};

export const CoverageForm: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <CoverageFormInner />
  </ThemeProvider>
);
